import React, { useState, useEffect } from 'react';
import { adminAPI } from '../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';
import './Usage.css';

const TABS = ['Overview', 'Daily', 'By Model', 'By User', 'Requests'];
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

function Usage() {
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [userData, setUserData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [typeData, setTypeData] = useState(null);

  // Filters
  const [days, setDays] = useState(30);
  const [userPage, setUserPage] = useState(0);
  const [requestPage, setRequestPage] = useState(0);
  const [requestHours, setRequestHours] = useState(24);

  // Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab, days, userPage, requestPage, requestHours]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'Overview':
          const [summaryRes, typeRes] = await Promise.all([
            adminAPI.getTokenSummary(),
            adminAPI.getTokensByType(days)
          ]);
          setSummary(summaryRes);
          setTypeData(typeRes);
          break;
        case 'Daily':
          const dailyRes = await adminAPI.getDailyTokenUsage(days);
          setDailyData(dailyRes);
          break;
        case 'By Model':
          const modelRes = await adminAPI.getTokensByModel(days);
          setModelData(modelRes);
          break;
        case 'By User':
          const userRes = await adminAPI.getUserTokenStats({ skip: userPage * 20, limit: 20 });
          setUserData(userRes);
          break;
        case 'Requests':
          const reqRes = await adminAPI.getTokenRequests({ skip: requestPage * 50, limit: 50, hours: requestHours });
          setRequests(reqRes);
          break;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (user) => {
    setSelectedUser(user);
    setDetailsLoading(true);
    try {
      const details = await adminAPI.getUserTokenDetails(user.user_id, 12);
      setUserDetails(details);
    } catch (err) {
      console.error('Failed to load user details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatTokens = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  return (
    <div className="usage-page">
      <div className="page-header">
        <h1 className="page-title">Usage Analytics</h1>
        <p className="page-subtitle">Monitor token usage, costs, and patterns across all users</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchData}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading...</span>
        </div>
      ) : (
        <>
          {activeTab === 'Overview' && summary && (
            <OverviewTab summary={summary} typeData={typeData} formatTokens={formatTokens} />
          )}
          {activeTab === 'Daily' && dailyData && (
            <DailyTab data={dailyData} days={days} setDays={setDays} formatTokens={formatTokens} />
          )}
          {activeTab === 'By Model' && modelData && (
            <ModelTab data={modelData} days={days} setDays={setDays} formatTokens={formatTokens} />
          )}
          {activeTab === 'By User' && (
            <UserTab
              data={userData}
              page={userPage}
              setPage={setUserPage}
              onViewDetails={fetchUserDetails}
              formatTokens={formatTokens}
            />
          )}
          {activeTab === 'Requests' && (
            <RequestsTab
              data={requests}
              page={requestPage}
              setPage={setRequestPage}
              hours={requestHours}
              setHours={setRequestHours}
              formatTokens={formatTokens}
            />
          )}
        </>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          details={userDetails}
          loading={detailsLoading}
          onClose={() => { setSelectedUser(null); setUserDetails(null); }}
          formatTokens={formatTokens}
        />
      )}
    </div>
  );
}

function OverviewTab({ summary, typeData, formatTokens }) {
  const pieData = typeData?.by_type?.map((t, i) => ({
    name: t.request_type,
    value: t.total_tokens,
    color: COLORS[i % COLORS.length]
  })) || [];

  return (
    <>
      {/* Summary Cards */}
      <div className="stats-grid stats-grid-5">
        <div className="stat-card highlight">
          <div className="stat-label">Total Tokens (All Time)</div>
          <div className="stat-value">{formatTokens(summary.all_time.total_tokens)}</div>
          <div className="stat-sub">
            {formatTokens(summary.all_time.input_tokens)} in / {formatTokens(summary.all_time.output_tokens)} out
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">This Month</div>
          <div className="stat-value">{formatTokens(summary.this_month.total_tokens)}</div>
          <div className="stat-sub">{summary.this_month.total_queries?.toLocaleString()} queries</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Today</div>
          <div className="stat-value">{formatTokens(summary.today.total_tokens)}</div>
          <div className="stat-sub">{summary.today.total_requests?.toLocaleString()} requests</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Input/Request</div>
          <div className="stat-value">{formatTokens(summary.averages.input_per_request)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Output/Request</div>
          <div className="stat-value">{formatTokens(summary.averages.output_per_request)}</div>
        </div>
      </div>

      {/* Token Distribution by Type */}
      {pieData.length > 0 && (
        <div className="chart-section">
          <h3>Token Usage by Request Type</h3>
          <div className="chart-row">
            <div className="chart-wrapper pie-chart">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatTokens(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="type-stats">
              {typeData?.by_type?.map((t, i) => (
                <div key={t.request_type} className="type-stat-item">
                  <div className="type-color" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                  <div className="type-info">
                    <div className="type-name">{t.request_type}</div>
                    <div className="type-details">
                      {t.request_count} requests | {formatTokens(t.input_tokens)} in | {formatTokens(t.output_tokens)} out
                    </div>
                  </div>
                  <div className="type-total">{formatTokens(t.total_tokens)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DailyTab({ data, days, setDays, formatTokens }) {
  return (
    <>
      <div className="filter-bar">
        <label>Period:</label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="chart-section">
        <h3>Daily Token Usage</h3>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#94a3b8" tickFormatter={formatTokens} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                formatter={(value) => formatTokens(value)}
              />
              <Legend />
              <Area type="monotone" dataKey="input_tokens" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} name="Input" />
              <Area type="monotone" dataKey="output_tokens" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Output" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="chart-section">
        <h3>Daily Requests</h3>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
              <Bar dataKey="request_count" fill="#8b5cf6" name="Requests" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

function ModelTab({ data, days, setDays, formatTokens }) {
  const chartData = data.models.slice(0, 15);

  return (
    <>
      <div className="filter-bar">
        <label>Period:</label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="chart-section">
        <h3>Token Usage by Model</h3>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={Math.max(400, chartData.length * 40)}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis type="number" stroke="#94a3b8" tickFormatter={formatTokens} />
              <YAxis type="category" dataKey="model_id" stroke="#94a3b8" width={180} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                formatter={(value) => formatTokens(value)}
              />
              <Legend />
              <Bar dataKey="input_tokens" stackId="a" fill="#6366f1" name="Input" />
              <Bar dataKey="output_tokens" stackId="a" fill="#10b981" name="Output" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Model</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Total Tokens</th>
              <th>Requests</th>
            </tr>
          </thead>
          <tbody>
            {data.models.map((model) => (
              <tr key={model.model_id}>
                <td><code>{model.model_id}</code></td>
                <td>{formatTokens(model.input_tokens)}</td>
                <td>{formatTokens(model.output_tokens)}</td>
                <td><strong>{formatTokens(model.total_tokens)}</strong></td>
                <td>{model.request_count?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UserTab({ data, page, setPage, onViewDetails, formatTokens }) {
  return (
    <>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>All Time Tokens</th>
              <th>This Month Tokens</th>
              <th>All Time Queries</th>
              <th>This Month Queries</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((user) => (
              <tr key={user.user_id}>
                <td>
                  <div className="user-cell">
                    <div className="user-name">{user.name || 'No name'}</div>
                    <div className="user-email">{user.email}</div>
                  </div>
                </td>
                <td>
                  <div className="token-cell">
                    <span className="token-total">{formatTokens(user.all_time.total_tokens)}</span>
                    <span className="token-breakdown">
                      {formatTokens(user.all_time.input_tokens)} / {formatTokens(user.all_time.output_tokens)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="token-cell">
                    <span className="token-total">{formatTokens(user.current_month.total_tokens)}</span>
                    <span className="token-breakdown">
                      {formatTokens(user.current_month.input_tokens)} / {formatTokens(user.current_month.output_tokens)}
                    </span>
                  </div>
                </td>
                <td>{user.all_time.query_count?.toLocaleString()}</td>
                <td>{user.current_month.query_count?.toLocaleString()}</td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => onViewDetails(user)}>
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
          Previous
        </button>
        <span className="pagination-info">Page {page + 1}</span>
        <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={data.length < 20}>
          Next
        </button>
      </div>
    </>
  );
}

function RequestsTab({ data, page, setPage, hours, setHours, formatTokens }) {
  return (
    <>
      <div className="filter-bar">
        <label>Time Range:</label>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          <option value={1}>Last hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={48}>Last 48 hours</option>
          <option value={168}>Last week</option>
        </select>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Type</th>
              <th>Input</th>
              <th>Output</th>
              <th>Total</th>
              <th>Models Used</th>
            </tr>
          </thead>
          <tbody>
            {data.map((req) => (
              <tr key={req.id}>
                <td className="nowrap">{new Date(req.created_at).toLocaleString()}</td>
                <td>
                  <div className="user-email-small">{req.user_email || req.user_id?.slice(0, 8)}</div>
                </td>
                <td>
                  <span className={`badge badge-${getTypeBadge(req.request_type)}`}>
                    {req.request_type}
                  </span>
                </td>
                <td>{formatTokens(req.input_tokens)}</td>
                <td>{formatTokens(req.output_tokens)}</td>
                <td><strong>{formatTokens(req.total_tokens)}</strong></td>
                <td>
                  <div className="models-used">
                    {req.model_breakdown?.slice(0, 3).map((m, i) => (
                      <span key={i} className="model-tag">{m.model?.split('/')[1] || m.model}</span>
                    ))}
                    {req.model_breakdown?.length > 3 && (
                      <span className="model-tag">+{req.model_breakdown.length - 3}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
          Previous
        </button>
        <span className="pagination-info">Page {page + 1}</span>
        <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={data.length < 50}>
          Next
        </button>
      </div>
    </>
  );
}

function UserDetailsModal({ user, details, loading, onClose, formatTokens }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Token Usage: {user.email}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading"><div className="spinner"></div><span>Loading...</span></div>
          ) : details ? (
            <>
              {/* Monthly Chart */}
              <div className="chart-section">
                <h4>Monthly Token Usage</h4>
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={[...details.monthly_history].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" tickFormatter={formatTokens} />
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        formatter={(value) => formatTokens(value)}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="input_tokens" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} name="Input" />
                      <Area type="monotone" dataKey="output_tokens" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Output" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Monthly Table */}
              <h4>Monthly Breakdown</h4>
              <div className="table-container small">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Queries</th>
                      <th>Input</th>
                      <th>Output</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.monthly_history.map((m) => (
                      <tr key={m.month}>
                        <td>{m.month}</td>
                        <td>{m.query_count}</td>
                        <td>{formatTokens(m.input_tokens)}</td>
                        <td>{formatTokens(m.output_tokens)}</td>
                        <td><strong>{formatTokens(m.total_tokens)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recent Requests */}
              <h4>Recent Requests</h4>
              <div className="table-container small">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Input</th>
                      <th>Output</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.recent_requests.slice(0, 20).map((r) => (
                      <tr key={r.id}>
                        <td className="nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td><span className={`badge badge-${getTypeBadge(r.request_type)}`}>{r.request_type}</span></td>
                        <td>{formatTokens(r.input_tokens)}</td>
                        <td>{formatTokens(r.output_tokens)}</td>
                        <td><strong>{formatTokens(r.total_tokens)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p>No data available</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getTypeBadge(type) {
  switch (type) {
    case 'council': return 'info';
    case 'dual': return 'warning';
    case 'single': return 'success';
    default: return 'secondary';
  }
}

export default Usage;
